<template>
  <div data-page-name="aces-program">
    <section class="hero">
      <div class="wrapper">
        <div class="content">
          <SvgIcon name="ace" class="ace" />
          <h1>{{ $t('aces-program.hero.title') }}</h1>
          <p>{{ $t('aces-program.hero.description') }}</p>
        </div>

        <NuxtImg
          :alt="$t('aces-program.hero.image-alt')"
          preload
          src="/redesign/images/aces-program-hero.webp"
          sizes="xs: md: xl:1028px"
          densities="1x 2x"
          format="webp"
        />
      </div>
    </section>

    <section class="about-the-program">
      <div class="wrapper">
        <div class="left">
          <h2>{{ $t('aces-program.about-the-program.title') }}</h2>
          <p>{{ $t('aces-program.about-the-program.description-1') }}</p>
          <p>{{ $t('aces-program.about-the-program.description-2') }}</p>
        </div>

        <div class="right">
          <NuxtImg
            :alt="$t('aces-program.about-the-program.image-alt')"
            src="/redesign/images/photos/aces-team-photo-1.webp"
            sizes="xs:304px md:704px xl:592px"
            densities="x1 x2"
            format="webp"
          />
        </div>
      </div>
    </section>

    <ul class="points">
      <li class="point values">
        <NuxtImg
          :alt="$t('aces-program.values.image-alt')"
          class="figure cube"
          src="/redesign/images/figures/figure_cube_view_01_color_lilac.webp"
          sizes="xs: md: xl:515px"
        />

        <SvgIcon name="heart-colorfull" />
        <h3>{{ $t('aces-program.values.title') }}</h3>
        <p>{{ $t('aces-program.values.description') }}</p>

        <ul class="list">
          <li class="item">
            <SvgIcon name="check" class="check purple" />
            <p>{{ $t('aces-program.values.list.item-1') }}</p>
          </li>

          <li class="item">
            <SvgIcon name="check" class="check purple" />
            <p>{{ $t('aces-program.values.list.item-2') }}</p>
          </li>

          <li class="item">
            <SvgIcon name="check" class="check purple" />
            <p>{{ $t('aces-program.values.list.item-3') }}</p>
          </li>
        </ul>
      </li>

      <li class="point benefits">
        <NuxtImg
          :alt="$t('aces-program.benefits.image-alt')"
          class="figure octahedron"
          src="/redesign/images/figures/figure_pyramid_view_03_color_orange.webp"
          sizes="xs: md: xl:515px"
        />

        <SvgIcon name="like-colorfull" />
        <h3>{{ $t('aces-program.benefits.title') }}</h3>
        <p>{{ $t('aces-program.benefits.description') }}</p>

        <ul class="list">
          <li class="item">
            <SvgIcon name="check" class="check orange" />
            <p>{{ $t('aces-program.benefits.list.item-1') }}</p>
          </li>

          <li class="item">
            <SvgIcon name="check" class="check orange" />
            <p>{{ $t('aces-program.benefits.list.item-2') }}</p>
          </li>

          <li class="item">
            <SvgIcon name="check" class="check orange" />
            <p>{{ $t('aces-program.benefits.list.item-3') }}</p>
          </li>
        </ul>
      </li>
    </ul>

    <section class="meet-aces">
      <div class="wrapper">
        <h4 class="title">{{ $t('aces-program.meet-aces.title') }}</h4>
        <p class="description">
          {{ $t('aces-program.meet-aces.description') }}
        </p>

        <ul class="columns">
          <li
            v-for="(column, columnIndex) of acesByColumns"
            :key="`column-${columnIndex}`"
            class="column"
          >
            <ul class="cards">
              <Cards.Aces
                v-for="(card, cardIndex) of column"
                v-bind="card"
                :key="`card-${cardIndex}`"
                tag="li"
              />
            </ul>
          </li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
// components
import Cards from '~/components/redesign/Cards';

// composables
import useApiSeo from '~/composables/useApiSeo';

// types
import type { CardsAcesProps } from '~/components/redesign/Cards/Aces.vue';

const { error, data: member_cards } = await useAsyncGql({
  operation: 'MemberCards',
  options: {
    transform: data => data.member_cards.data as CardsAcesProps[],
  },
});

if (error.value) throw createError(error.value);
if (!member_cards.value)
  throw createError({
    status: 404,
    message: 'Page not found',
  });

const acesByColumns = computed(() => {
  const cards = member_cards.value ?? [];

  return cards.reduce(
    (columns, card, index) => {
      columns[index % 2].push(card);
      return columns;
    },
    [[], []],
  );
});

useApiSeo('aces-program');
</script>

<style scoped lang="scss">
@import '$/mixins/size';
@import '$/mixins/flex';
@import '$/mixins/components';
@import '$/mixins/typography';
@import '$/mixins/text-transform';
@import '$/functions/token';
@import '$/variables/shadows';

[data-page-name='aces-program'] {
  section.hero {
    overflow: hidden;
    background-color: token('surf-cont-secon-action');

    margin-bottom: 8rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 2.5rem;
    }

    .wrapper {
      position: relative;
      padding: 5rem 2rem 8rem 2rem;

      @include wrapper(80rem);

      @include mobile {
        padding: 5rem 1rem 5rem 1rem;
      }

      .content {
        position: relative;
        z-index: 1;

        color: token('text-reversed');
        @include fluid-max-width(37rem);

        @include tablet {
          @include fluid-max-width(26rem);
        }

        svg.ace {
          margin-bottom: 2.8rem;

          @include mobile {
            margin-bottom: 2.25rem;
          }
        }

        h1 {
          @include h2;
          margin-bottom: 2rem;

          @include mobile {
            margin-bottom: 1rem;
          }
        }

        p {
          @include body-1;
        }
      }

      img {
        position: absolute;
        top: 0;
        left: 50%;
        bottom: 0;
        z-index: 0;

        height: 100%;

        @include tablet {
          left: calc(50% - 2.2rem);
        }

        @include mobile {
          left: calc(50% - 3.4rem);
        }
      }
    }
  }

  section.about-the-program {
    margin-bottom: 8rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    @include mobile {
      margin-bottom: 2.6rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include flex-stretch-sb;
      gap: 0 2rem;

      @include tablet {
        flex-direction: column;
        gap: 3rem 0;
      }

      @include mobile {
        padding: 0 1rem;
        gap: 2rem 0;
      }

      .left,
      .right {
        width: 50%;

        @include tablet {
          width: 100%;
        }
      }

      .left {
        @include flex-start-start;
        flex-direction: column;
        gap: 2rem 0;

        @include tablet {
          gap: 1.5rem 0;
        }

        h2 {
          @include h2;
        }

        p {
          @include body-2;

          @include mobile {
            @include body-3;
          }
        }
      }

      .right {
        img {
          display: block;
          width: 100%;
          height: auto;
        }
      }
    }
  }

  ul.points {
    padding: 0 2rem;
    @include wrapper(80rem);

    @include flex-stretch-sb;
    gap: 0 2rem;

    margin-bottom: 8rem;

    @include tablet {
      gap: 0 1.5rem;
      margin-bottom: 5rem;
    }

    @include mobile {
      flex-direction: column;
      gap: 2.5rem 0;
      padding: 0 1rem;
      margin-bottom: 5rem;
    }

    li.point {
      position: relative;
      overflow: hidden;

      padding: 4rem;
      border: 1px solid token('outline-secondary');

      @include flex-start-start;
      flex-direction: column;
      gap: 2rem 0;

      @include tablet {
        padding: 1.5rem;
        gap: 1.5rem 0;
      }

      @include mobile {
        padding: 1.5rem 1rem;
      }

      img.figure {
        position: absolute;
        top: 0;
        right: 0;

        &.cube {
          transform: translate(calc(50% - 7.35rem), calc(-50% + 0.7rem))
            rotate(-69.5deg);

          @include tablet {
            transform: translate(calc(50% - 3.75rem), calc(-50% - 0.55rem))
              rotate(-69.5deg) scale(0.56);
          }

          @include mobile {
            transform: translate(calc(50% - 3.92rem), calc(-50% + 3.48rem))
              rotate(-69.5deg) scale(0.495);
          }
        }

        &.octahedron {
          transform: translate(calc(50% - 6.6rem), calc(-50% + 0.85rem))
            rotate(47.5deg) scaleY(-1);

          @include tablet {
            transform: translate(calc(50% - 4.33rem), calc(-50% - 0.26rem))
              rotate(47.5deg) scaleY(-1) scale(0.56);
          }

          @include mobile {
            transform: translate(calc(50% - 4.13rem), calc(-50% - 1.73rem))
              rotate(47.5deg) scaleY(-1) scale(0.74);
          }
        }
      }

      svg {
        @include fixed-size(3.125rem);
      }

      h3 {
        @include h3;
      }

      p {
        @include subtitle-2;
      }

      ul.list {
        @include flex-start-start;
        flex-direction: column;
        gap: 2rem 0;

        @include tablet {
          gap: 1.5rem 0;
        }

        li.item {
          @include flex-start-start;
          gap: 0 0.75rem;
          padding-bottom: 1rem;

          width: 100%;
          border-bottom: 1px solid token('outline-secondary');

          @include mobile {
            gap: 0 0.7rem;
          }

          svg {
            @include fixed-size(1.25rem);
            border-radius: 100px;
            fill: token('icon-reversed');

            transform: translate(0.1rem, 0.1rem);

            &.purple {
              background-color: token('surf-cont-tert-action');
            }

            &.orange {
              background-color: token('icon-action');
            }
          }

          p {
            @include body-3;
          }
        }
      }
    }
  }

  section.meet-aces {
    margin-bottom: 11.4rem;

    @include tablet {
      margin-bottom: 5rem;
    }

    .wrapper {
      padding: 0 2rem;
      @include wrapper(80rem);

      @include mobile {
        padding: 0 1rem;
      }

      h4.title {
        @include h3;
        margin-bottom: 2rem;

        @include tablet {
          margin-bottom: 1.5rem;
        }

        @include mobile {
          margin-bottom: 1rem;
        }
      }

      p.description {
        @include fluid-max-width(49rem);
        @include body-2;
        margin-bottom: 3rem;

        @include tablet {
          margin-bottom: 2.5rem;
        }

        @include mobile {
          @include body-3;
        }
      }

      ul.columns {
        @include flex-start-sb;
        gap: 0 1.7rem;

        @include tablet {
          flex-wrap: wrap;
        }

        li.column {
          width: 50%;

          @include tablet {
            width: 100%;

            &:not(:last-child) {
              margin-bottom: 2rem;
            }
          }
        }
      }
    }
  }
}
</style>
